import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Stack,
  Button,
  Checkbox,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CollectPaymentModal, PaymentDetails, PaymentFormValues, PaymentHistoryItem } from '../../../collectPayment';
import { getPackageConfigConfigURL, getRefundListsURL, InvoiceRefundRow, PaymentDetailPayload, RefundListItem, RefundPaymentPayload } from '../../Refund.helper';
import { useMutation } from '@tanstack/react-query';
import { getRequest, postRequest } from '../../../../../services/httpService';
import { postMakeAdvancePaymentURL } from '../../../Advance/advance.urls';
import useCurrency from '../../../../../components/CurrencyProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { useClinic } from '../../../../../components/ClinicProvider';
import { formatDateDMY } from '../../../../../utils/dateUtils';
import { usePatientContext } from '../../../../../components/patinetProvider';
import { useAlert } from '../../../../../components/alertProvider';
import { useAppQuery } from '../../../../../services/appQuery';
import style from '../../../Advance/Advance.module.css';
import { CHECKED_ICON, UNCHECKED_ICON } from '../../../Invoice/common/InvoiceTableCommon';
import TableSkeleton from '../../../../../components/common/TableSkeleton';
import { AdvanceConfigLookups } from '../../../Advance/advance.interfaces';
import { useTranslation } from 'react-i18next';

const ProceedAdvance: React.FC = () => {

  const { formatCurrency, currency: providerCurrency } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const { refund_id } = useParams<{ id?: string; refund_id?: string }>();
  const refundId = Number(refund_id ?? 0);
  const { patientId , MainpatientDetails } = usePatientContext();
  const {ClinicId} = useClinic();
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const { addAlert } = useAlert();
  const navigate = useNavigate();
  const toSafeNumber = (value: unknown) => Number(value) || 0;
  const patientDisplayName = `${MainpatientDetails?.first_name ?? ''} ${MainpatientDetails?.last_name ?? ''}`.trim() || tBilling('RefundDetails.common.patientFallback', 'Patient');
  const patientRefundRoute = patientId ? `/admin/billing/refund/patient-refund/${patientId}` : '/admin/billing/refund/refund-list';

  const { data: requestedRefunds, isLoading } = useAppQuery<RefundListItem>(
    ['RequestedRefundDetails', refundId],
    () => getRequest<RefundListItem>(`${getRefundListsURL()}${refundId}/`),
    {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
    }
  );
  const { data: PackageConfig, queryClient } = useAppQuery<AdvanceConfigLookups>(
    ['PackageConfigMaster'],
    () => getRequest<AdvanceConfigLookups>(`${getPackageConfigConfigURL()}`),
    { 
      enabled: true,
    }
  );

  const invoiceRefundData: InvoiceRefundRow[] = useMemo(
    () => {
      if (!requestedRefunds) return [];
      return [{
        id: 1,
        date: requestedRefunds.date ??requestedRefunds.refund_date ??'-',
        receiptNo: requestedRefunds.receipt_no ?? '-',
        bill_advance_number: requestedRefunds.bill_advance_number ?? null,
        against: requestedRefunds.refund_against ?? '-',
        packageName: requestedRefunds.package_name !== '' ? requestedRefunds.package_name : '-',
        totalAmount: requestedRefunds.total_amount ?? '-',
        concession_amount: Number(requestedRefunds.concession_amount ?? 0),
        net_amount: Number(requestedRefunds.net_amount ?? 0),
        requested_amount: Number(requestedRefunds.requested_amount ?? 0),
      }];
    },
    [requestedRefunds]
  );

  const paginatedRows = invoiceRefundData;
  const pageIds = useMemo(() => paginatedRows.map((row) => row.id), [paginatedRows]);
  const allSelectedOnPage = useMemo(
    () => paginatedRows.length > 0 && paginatedRows.every((row) => selectedIds.includes(row.id)),
    [paginatedRows, selectedIds]
  );
  const someSelectedOnPage = useMemo(
    () => paginatedRows.some((row) => selectedIds.includes(row.id)),
    [paginatedRows, selectedIds]
  );


  const columns = useMemo(
    () => [
      '',
      tBilling('Refund.date', 'Date'),     
      tBilling('Refund.receipt_no', 'Bill/Advance Number'),
      tBilling('Advance.against', 'Against'),
      tBilling('Advance.package_name', 'Package Name'),
      tBilling('Refund.total_amt', 'Total Amount'),
      tBilling('Refund.concession_amt', 'Concession Amount'),
      tBilling('Refund.net_amt', 'Net Amount'),
      tBilling('Refund.requested_amt', 'Requested Refund'),
    ],
    [tBilling]
  );


  const handleRowSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleSelectAllCurrentPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  const totalInvoicedAmount = useMemo(
    () => invoiceRefundData.reduce((sum, row) => sum + toSafeNumber(row.totalAmount), 0),
    [invoiceRefundData]
  );

  const refundAmount = useMemo(
    () =>
      invoiceRefundData
        .filter((row) => selectedIds.includes(row.id))
        .reduce((sum, row) => sum + (row.requested_amount || 0), 0),
    [selectedIds, invoiceRefundData]
  );

  const remainingAmount = totalInvoicedAmount - refundAmount;

  const refundSummary = useMemo(
    () => [
      { label: tBilling('RefundDetails.summary.totalInvoicedAmount', 'Total Invoiced Amount'), value: totalInvoicedAmount.toFixed(2) },
      { label: tBilling('RefundDetails.summary.refundAmount', 'Refund Amount'), value: refundAmount.toFixed(2) },
      { label: tBilling('RefundDetails.summary.remainingAmount', 'Remaining Amount'), value: remainingAmount.toFixed(2) },
    ],
    [totalInvoicedAmount, refundAmount, remainingAmount, tBilling]
  );

  const { mutate: submitPayment, isPending: isSubmittingPayment } = useMutation({
    mutationFn: (payload: RefundPaymentPayload) => postRequest(postMakeAdvancePaymentURL(), payload),
    onSuccess: async () => {
      addAlert(tBilling('RefundDetails.messages.paymentSavedSuccess', 'Payment saved successfully.'), 'success');
      setCollectPaymentOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['RequestedRefundList'] });
      navigate(patientRefundRoute);
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || tBilling('RefundDetails.messages.paymentSaveFailure', 'Failed to save payment.');
      addAlert(message, 'error');
    },
  });

  const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[] = []) => {
    if (!refundId) {
      addAlert(tBilling('RefundDetails.messages.invalidRefundReference', 'Invalid refund reference.'), 'error');
      return;
    }
    if (!history.length) {
      addAlert(tBilling('RefundDetails.messages.missingPaymentHistory', 'Missing payment history. Please try again.'), 'error');
      return;
    }

    const currency = paymentDetailsForModal?.currency ?? 'INR';
    const isoOrNull = (d?: string) => (d ? new Date(d).toISOString() : new Date().toISOString());

    const paymentDetails: PaymentDetailPayload[] = history.map((item) => ({
      payment_mode_id: item.payment_mode_id,
      paid_amount: String(item.amount ?? 0),
      currency,
      number: item.transactionNumber ?? null,
      date: isoOrNull(item.date ?? undefined),
      bank_id: null,
      account_name: item.bankName ?? null,
      branch_name: null,
      ifsc_code: null,
      remarks: item.notes ?? null,
      cheque_number: item.chequeNumber ?? null,
      cheque_date: item.chequeDate ? new Date(item.chequeDate).toISOString() : null,
    }));

    const payload: RefundPaymentPayload = {
      patient_id: toSafeNumber(patientId),
      clinic_id: toSafeNumber(ClinicId),
      payment_against_type: 'REFUND',
      refund_id: refundId,
      refund_type: 'ADVANCE',
      refund_amount: refundAmount.toFixed(2),
      remarks: history[0]?.notes ?? '',
      payment_details: paymentDetails,
    };

    submitPayment(payload);
  };

  const handleOpenCollectPayment = (netAmount: number, patientName: string, patientIdValue: string) => {
    setPaymentDetailsForModal({
      date: new Date().toLocaleDateString('en-GB'),
      patientName: patientName || 'Patient',
      patientId: patientIdValue || '',
      patientNumericId: patientId ?? undefined,
      mrnNo: MainpatientDetails?.medical_record_no ?? '',
      currency: providerCurrency,
      netAmount: netAmount,
    });
    setCollectPaymentOpen(true);
  };
  
  return(
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="proceed-advance-container-table">
        <Table aria-label={tBilling('RefundDetails.aria.refundAdvanceTable', 'Refund advances list')} data-testid="proceed-advance-table-list">
          <TableHead>
            <TableRow sx={{ bgcolor: '#FAFAFA' }}>
              {columns.map((col, idx) => (
                <TableCell
                  key={idx === 0 ? 'select' : col}
                  className={style.tableHeader}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {idx === 0 ? (
                    <Checkbox
                      icon={UNCHECKED_ICON}
                      checkedIcon={CHECKED_ICON}
                      indeterminate={someSelectedOnPage && !allSelectedOnPage}
                      checked={allSelectedOnPage}
                      onChange={handleSelectAllCurrentPage}
                      data-testid="proceed-advance-checkbox-select-all"
                      slotProps={{
                        input: {
                          'aria-label': tBilling('RefundDetails.aria.selectAllAdvanceRows', 'Select all advance rows')
                        }
                      }}
                    />
                  ) : (
                    col
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableSkeleton
                columnWidths={[40, 120, 140, 160, 220, 140, 140, 140, 140]}
                rows={10}
              />
            )}

            {!isLoading &&
                  paginatedRows.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      tabIndex={0}
                      data-testid={`proceed-advance-row-${row.id}`}
                      aria-label={tBilling('RefundDetails.aria.advanceRow', 'Advance row')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRowSelect(row.id);
                        }
                      }}
                    >
                      <TableCell className={style.tableCells}>
                        <Checkbox
                          checked={selectedIds.includes(row.id)}
                          onChange={() => handleRowSelect(row.id)}

                          icon={UNCHECKED_ICON}
                          checkedIcon={CHECKED_ICON}
                          disableRipple
                          data-testid={`proceed-advance-checkbox-row-${row.id}`}
                          slotProps={{
                            input: {
                              'aria-label': tBilling('RefundDetails.aria.selectAdvanceRow', 'Select advance row')
                            }
                          }}
                        />
                       
                      </TableCell>
                      <TableCell className={style.tableCells}>{row.date ? formatDateDMY(row.date) : '-'}</TableCell>
                      <TableCell className={style.tableCells}>{row.bill_advance_number ?? '-'}</TableCell>
                      <TableCell className={style.tableCells}>{row.against ?? '-'}</TableCell>
                      <TableCell className={style.tableCells}>{row.packageName }</TableCell>
                      <TableCell className={style.tableCells}>{row.totalAmount ?? '-'}</TableCell>
                      <TableCell className={style.tableCells} >
                        {formatCurrency(row.concession_amount || 0)}
                      </TableCell>
                      <TableCell className={style.tableCells} >
                        {formatCurrency(row.net_amount ?? 0)}
                      </TableCell>
                      <TableCell className={style.tableCells} >
                        {formatCurrency(row.requested_amount ?? 0)}
                      </TableCell>

                    </TableRow>
                  ))}

            {!isLoading && paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">{tBilling('Refund.no_entries_found', 'No entries found.')}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
          
      </TableContainer>

      <Box sx={{ mt: 4, px: 1 }} data-testid="proceed-advance-container-summary">
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {tBilling('RefundDetails.summary.title', 'Refund Details')}
        </Typography>
        <Stack direction="row" spacing={6} sx={{ mb: 4 }} data-testid="proceed-advance-summary-values">
          {refundSummary.map((item) => (
            <Stack key={item.label} direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {item.label}
              </Typography>
              <Typography variant="body1">:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            sx={{ borderRadius: 3, minWidth: 120 }}
            disabled={isSubmittingPayment}
            onClick={() => navigate(patientRefundRoute)}
            data-testid="proceed-advance-button-cancel"
            aria-label={tBilling('RefundDetails.aria.cancelRefundPayment', 'Cancel refund payment')}
          >
            {tBilling('package_details.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={selectedIds.length === 0 || isSubmittingPayment}
            sx={{ borderRadius: 3, minWidth: 120 }}
            onClick={() => handleOpenCollectPayment(refundAmount ?? 0, patientDisplayName, String(patientId ?? ''))}
            data-testid="proceed-advance-button-refund-payment"
            aria-label={tBilling('RefundDetails.aria.openRefundPaymentModal', 'Open refund payment modal')}
          >
            {tBilling('RefundDetails.actions.refundPayment', 'Refund Payment')}
          </Button>
        </Stack>
      </Box>

      {paymentDetailsForModal && (
        <CollectPaymentModal
          open={collectPaymentOpen}
          onClose={() => setCollectPaymentOpen(false)}
          hidePaymentModeToggle={true}
          onSave={handlePaymentSave}
          paymentDetails={paymentDetailsForModal}
          existingPaymentHistory={[]}
          walletAdvances={[]}
          paymentModes={PackageConfig?.modeofpayment || []}
          title={tBilling('collectPayment.refundPaymentTitle', 'Refund Payment')}
          isSubmitting={isSubmittingPayment}
          showWallet={true}
          requireFullPayment={true}
        />
      )}
    </>
  );
};
export default ProceedAdvance;